import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { FundsListItemComponent } from './components/funds-list-item/funds-list-item.component';
import { ActivatedRoute, Params } from '@angular/router';
import { takeUntil } from 'rxjs';
import { FilterModel } from '../models/filter.model';
import { IFundProfileModel } from '../models/fund-profile.model';
import { sortMap } from './models/fund-sort-map.model';
import { SortDirection } from './models/sort-direction.enum';
import { BaseComponent } from '../../../components/core/components/base/base.component';
import { PortfolioDetail } from '../../../data-access/models/portfolio-detail.model';
import { FundDataService } from '../../../components/core/services/fund-data.service';
import { FundSort } from '../../../data-access/enums/fund-sort';
import { HOME_ROUTE, INVESTMENT_LIST_ROUTE } from '../../../data-access/constants/app-routes';
import { SortBottomSheetComponent } from '../../purchase/components/sort-bottom-sheet/sort-bottom-sheet.component';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { LocationService } from '../../../shared/services/location.service';
import { BackToOriginService } from '../../../shared/services/back-to-origin.service';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { IntroductionBottomSheetComponent } from './components/introduction-bottom-sheet/introduction-bottom-sheet.component';
import { IIntroductionData } from './models/introduction.interface';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { NgxIcon } from '@digipay/ngx-icon';
import { IFundIntroduction } from './models/fund-introduction.interface';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';

@Component({
  selector: 'app-funds-list',
  standalone: true,
  imports: [NgxButtonComponent, FundsListItemComponent, NgxAppBarComponent, NgxIcon, SpinnerComponent],
  templateUrl: './funds-list.component.html',
  styleUrl: './funds-list.component.scss',
})
export class FundsListComponent extends BaseComponent implements OnInit, OnDestroy {
  assetData = signal<PortfolioDetail[] | undefined>(undefined);
  funds = signal<IFundProfileModel[] | undefined>(undefined);
  fundsFilter = signal<FilterModel<FundSort> | undefined>(undefined);
  isLoading = signal<boolean>(true);
  qParams = signal<Params | undefined>(undefined);
  pageTitle = signal<string | undefined>(undefined);
  fundIntroduction = signal<IFundIntroduction | undefined>(undefined);

  private navigationService = inject(WealthNavigationService);
  private backHandler = inject(BackHandlerService);
  private bottomSheet = inject(NgxBottomSheetService);
  private activatedRoute = inject(ActivatedRoute);
  private fundDataService = inject(FundDataService);
  private location = inject(LocationService);
  private backToOrigin = inject(BackToOriginService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.fundsFilter.set({
      Column: FundSort.Default,
      SortDirection: SortDirection.Ascending,
    });
    this.qParams.set(this.activatedRoute.snapshot.queryParams);

    this.fundIntroduction.set({
      type: this.qParams()['type']?.toLowerCase(),
      imageUrl: this.getImageUrl(this.qParams()['type']?.toLowerCase()),
      title: this.getTitle(this.qParams()['type']?.toLowerCase()),
    });

    const type = this.qParams()['type']?.toLowerCase() || 'fixedincome';
    this.pageTitle.set(
      type === 'fixedincome'
        ? 'صندوق‌های درامد ثابت'
        : type === 'index'
          ? 'صندوق‌های شاخصی'
          : type === 'gold'
            ? 'سرمایه‌گذاری مبتنی بر طلا'
            : 'صندوق‌های درامد ثابت',
    );

    this.getAssets();
    this.fundDataService
      .getAllFunds(this.fundsFilter(), true, this.qParams()['type'] || 'FixedIncome')
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res?.success) {
          this.funds.set(res.result);
        }

        this.isLoading.set(false);
      });

    if (this.qParams()['referrer']) {
      this.backHandler.setCustomBackUrl('hub');
    }
  }

  override ngOnDestroy() {
    this.backHandler.setCustomBackUrl(undefined);
  }

  getAssets() {
    this.fundDataService.getUserAssets().subscribe({
      next: (res) => {
        if (res && res.portfolioDetails) {
          this.assetData.set(res.portfolioDetails);
        } else {
          console.error('Invalid response:', res);
        }
      },
      error: (err) => {
        console.error('Error fetching user assets:', err);
      },
    });
  }

  showIntroduction(type: string) {
    let data: IIntroductionData;

    if (type === 'gold') {
      data = {
        image: 'wealth-assets/images/gold-background-bottomsheet.png',
        title: 'صندوق‌طلا چیست؟',
        bullets: [
          'صندوق های طلا راهی ساده و مطمئن برای سرمایه گذاری در بازار طلا هستند.',
          ' دارایی این صندوق ها ترکیبی از سکه و شمش طلا است که میزان هرکدام در صندوق های مختلف متفاوت است. ارزش آن ها متناسب با قیمت روز طلا نوسان دارد و با سرمایه گذاری در این صندوق ها، می توانید بدون دغدغه نگهداری و امنیت، از تغییرات قیمت طلا بهره مند شوید.',
          'در این صندوق ها، طلای فیزیکی خریداری شده و در خزانه های بانک و بورس کالا نگهداری می شود؛ برای این طلاها گواهی هایی صادر می شود که سرمایه گذاران با خرید آن ها، مالکیت معادل فیزیکی طلا را به دست می آورند.',
          'این صندوق ها تحت مدیریت معتبرترین نهادهای مالی و نظارت سازمان بورس و اوراق بهادار فعالیت می کنند، که این امر اطمینان و شفافیت بیشتری را برای سرمایه گذاران فراهم می سازد.',
        ],
      };
    } else if (type === 'index') {
      data = {
        image: 'wealth-assets/images/index-background.png',
        title: 'صندوق شاخصی چیست؟',
        descriptions: [
          'در زیر نمودار صندوق تجارت شاخصی کاردان از تاریخ ۱۴۰۳/۰۸/۰۱ تا ۱۴۰۳/۱۰/۱۸ به عنوان نمونه آورده شده است:',
          'همانطور که در این نمودار مشاهده می‌کنید، کاهش یا افزایش ارزش این صندوق هم‌راستای ارزش شاخص کل بورس بوده و در بازه زمانی ۲ ماه، 52٪ بازدهی داشته است. این در حالی است که بازدهی شاخص کل بورس در این بازه ۴۷٪ بوده است.',
        ],
        bullets: [
          'با سرمایه گذاری در این صندوق ها، می توانید بازدهی متناسب با شاخص کل بورس را تجربه کنید.',
          'ریسک این صندوق ها در مقایسه با صندوق‌های درامد ثابت بیشتر است.',
          'بدیهی است مبلغ سرمایه گذاری شما متناسب با شاخص کل بورس، افزایش یا کاهش می‌یابد.',
        ],
        chartImage: 'wealth-assets/images/index-fund-chart.png',
      };
    } else if (type === 'fixedincome') {
      data = {
        image: 'wealth-assets/images/fixedincome-background.png',
        title: 'صندوق درامد ثابت چیست؟',
        bullets: [
          'صندوق های درآمد ثابت یکی از کم ریسک ترین روش های سرمایه گذاری هستند که عمده دارایی آن ها در اوراق بدهی و سپرده های بانکی قرار دارد.',
          'این صندوق ها در دو نوع صدور و ابطالی و ETF (قابل معامله) عرضه می شوند.',
          'برخی از آن ها دارای تقسیم سود ماهانه هستند و سود مشخصی را هر ماه به حساب سجام سرمایه گذاران واریز می کنند، در حالی که برخی دیگر سود را به سرمایه اصلی اضافه کرده و در زمان ابطال، مجموع آن را پرداخت می کنند.',
          ' به دلیل ماهیت دارایی هایشان، این صندوق ها نوسان قیمتی زیادی ندارند و گزینه ای مطمئن برای سرمایه گذاری محسوب می شوند.',
        ],
      };
    }

    this.bottomSheet.openBottomSheet(IntroductionBottomSheetComponent, { data: data });
  }

  onBackHandler() {
    this.checkEnterMode(this.location.lastRoute);
  }

  onSort() {
    this.bottomSheet.openBottomSheet(SortBottomSheetComponent, { data: this.fundsFilter()?.Column });

    const bottomSheetService = this.bottomSheet.onClose.subscribe(() => {
      bottomSheetService.unsubscribe();
      const result = this.bottomSheet.outputData();
      this.onSortChanged(result);
    });
  }

  onSortChanged(value: FundSort) {
    this.updateFilter(value);
    this.fundDataService
      .getAllFunds(this.fundsFilter(), true, this.qParams()['type'])
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res?.success) {
          this.funds.set(res.result);
        }
      });
  }

  private updateFilter(value: FundSort) {
    this.fundsFilter.set(this.getFundsFilter(value));
  }

  private getFundsFilter(value: FundSort): FilterModel<FundSort> {
    return sortMap[value];
  }

  checkEnterMode(mode: string) {
    const url = mode?.split('?')[0];
    const q = {
      type: this.qParams()['type'],
    };
    if (this.qParams()['referrer']) {
      q['referrer'] = this.qParams()['referrer'];
    }
    if (this.qParams()['referrer']) {
      this.backToOrigin.goBackToOrigin();
    } else {
      switch (url) {
        case '/home':
          this.navigationService.navigate([HOME_ROUTE]);
          break;
        case null:
        case undefined:
          this.backToOrigin.goBackToOrigin();
          break;
        case '/investments':
          this.navigationService.navigateWithQueryParams([INVESTMENT_LIST_ROUTE], {
            queryParams: q,
          });
          break;
        case 'refresh':
        default:
          this.navigationService.navigate([HOME_ROUTE]);
      }
    }
  }

  private getImageUrl(type: string): string {
    switch (type) {
      case 'fixedincome':
        return 'wealth-assets/images/fixedincome-background.png';
      case 'gold':
        return 'wealth-assets/images/gold-background.png';
      case 'index':
        return 'wealth-assets/images/index-background.png';
      default:
        return '';
    }
  }

  private getTitle(type: string): string {
    switch (type) {
      case 'fixedincome':
        return 'اطلاعاتت رو درباره صندوق درامد ثابت بیشتر کن!';
      case 'gold':
        return 'میخوای درباره صندوق طلا بیشتر بدونی؟';
      case 'index':
        return 'اطلاعاتت رو درباره صندوق شاخصی بیشتر کن!';
      default:
        return '';
    }
  }
}
